<template>
  <div class="w-[28rem] h-[420px]">
    <NText variant="caption1" weight="bold">
      {{ t('symbol.recent_broker_change_requests') }}
    </NText>

    <div
      v-if="isLoadingDepositReceipts"
      class="w-full mt-1 flex items-center justify-center"
    >
      <NLoading class="text-brand-primary" />
    </div>

    <div v-else class="max-h-[27rem] h-full mt-1 flex flex-col">
      <NTable
        v-if="depositReceipts.length"
        ref="loadMoreRef"
        :data="depositReceipts"
        cell-padding="small"
        :striped="false"
        :column-splitter="false"
        fixed-header
        identifier="requestId"
        class="mt-1 max-h-96 basis-[fit-content]"
      >
        <NTableColumn v-slot="{ row }">
          <NegarInfoWithBorderIcon
            size="20"
            class="cursor-pointer"
            @click="openDepositReceiptHistoryInfoDialog(row)"
          />
        </NTableColumn>

        <NTableColumn
          v-slot="{ row }"
          :label="`${t('common.amount')} (${t('misc.rial')})`"
          prop="depositValue"
          sortable
          align="text-right"
        >
          <Number
            :key="row.depositValue"
            #="{ number }"
            :value="row.depositValue"
          >
            <NText variant="caption1">
              {{ number }}
            </NText>
          </Number>
        </NTableColumn>

        <NTableColumn
          v-slot="{ row }"
          :label="t('symbol.broker_change_request_time')"
          prop="requestEntryDate"
          sortable
          align="text-right"
        >
          <NText variant="caption1">
            {{ creationTime(row.requestEntryDate) }}
          </NText>
        </NTableColumn>

        <NTableColumn
          v-slot="{ row }"
          :label="t('common.status')"
          prop="requestStateId"
          sortable
          align="text-right"
        >
          <NText
            :key="row.requestState"
            variant="caption1"
            :class="{
              'text-brand-primary': REQUEST_STATUS_ID.done.includes(
                row.requestStateId
              ),
              'text-critical-primary': REQUEST_STATUS_ID.failed.includes(
                row.requestStateId
              ),
              'text-neutral-primary': REQUEST_STATUS_ID.canceled.includes(
                row.requestStateId
              ),
            }"
          >
            {{ row.requestState }}
          </NText>
        </NTableColumn>

        <template #hoveredElements="{ row }">
          <NegarTrashLightIcon
            v-if="isDepositReceiptRequested(row.requestStateId)"
            ref="deleteDepositReceiptButtonRef"
            size="20"
            class="text-critical-primary ml-2 cursor-pointer"
            @click="handleOpenDeleteDialog(row, $event.target)"
          />
        </template>
      </NTable>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDialog, useToast } from '@negar/core';
import { NegarTrashLightIcon, NegarInfoWithBorderIcon } from '@negar/icons';
import { GetBrokerChangeItem } from '@libs/api-reference';
import { useQueryClient } from '@libs/use';
import {
  CASH_QUERY_KEYS,
  REQUEST_STATUS_ID,
  useDeleteDepositReceiptQuery,
  useGetDepositReceiptsInfiniteQuery,
} from '@core/cash';

import { useCashDialogs } from '@/modules/cash';

import Number from '@/components/Number.vue';

const deleteDepositReceiptButtonRef = ref<HTMLElement>();

const { t, d } = useI18n();

const toast = useToast();
const { remove: removeDeleteDialog } = useDialog();

const { openDepositReceiptDeleteDialog, openDepositReceiptHistoryInfoDialog } =
  useCashDialogs();

const queryClient = useQueryClient();

const { depositReceipts, isLoadingDepositReceipts, loadMoreRef } =
  useGetDepositReceiptsInfiniteQuery();

const { deleteReceipt, isLoadingDeleteReceipt } = useDeleteDepositReceiptQuery({
  onSuccess() {
    toast.neutral({
      message: t('modules.cash.register_deposit_receipt.cancel_done_message'),
      duration: 3000,
    });

    removeDeleteDialog();

    queryClient.invalidateQueries(CASH_QUERY_KEYS.depositReceipt);
  },
  onReject() {
    toast.negative({
      message: t('modules.cash.register_deposit_receipt.cancel_failed_message'),
      duration: 3000,
    });
  },
});

const isDepositReceiptRequested = (requestStateId: number) => {
  return requestStateId === 1;
};

const creationTime = (date) =>
  `${d(date, 'shortDate')} ,
    ${d(date, 'shortTime')}`;

const handleOpenDeleteDialog = (
  brokerChangeItem: GetBrokerChangeItem,
  deleteDepositReceiptButtonRef: HTMLElement
) => {
  openDepositReceiptDeleteDialog({
    isLoadingDeleteReceipt,
    brokerChangeItem,
    deleteReceipt,
    removeDeleteDialog,
    deleteDepositReceiptButtonRef,
  });
};
</script>
